import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useRevenuePerMember from 'hooks/reports/tbb-metrics/useRevenuePerMember';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { RevenuePerMemberReport } from 'types';

const ArmReportTable = ({
  type,
  month,
  setOrderingValue,
}: {
  type: 'total' | 'paying';
  month: Date | null;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();
  const { formatCurrency } = useLocalization();

  const [tableData, setTableData] = useState<RevenuePerMemberReport[]>([]);
  const [ordering, setOrdering] = useState('revenue');
  const [ascending, setAscending] = useState(false);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useRevenuePerMember({
      month: dayjs(month).format('YYYY-MM'),
      type: type,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  useEffect(() => {
    setOrderingValue(ascending ? ordering : `-${ordering}`);
  }, [ordering, ascending, setOrderingValue]);

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const handleNameClick = useCallback(
    (athlete: RevenuePerMemberReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'fullName' as const,
      Cell: ({ row }: CellProps<RevenuePerMemberReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original)}
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Current Status',
      accessor: 'athleteStatus' as const,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
    },
    {
      Header: 'Revenue',
      accessor: 'revenue' as const,
      Cell: ({ value }: CellProps<RevenuePerMemberReport>) => formatCurrency(value / 100),
    },
  ];

  return (
    <Table
      tableData={tableData}
      columns={columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={['fullName', 'email', 'phone', 'revenue']}
      handleHeaderClick={handleHeaderClick}
      ordering={ordering}
      ascending={ascending}
    />
  );
};

export default ArmReportTable;
